import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    IconButton
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import { Order } from '../../../app/models/order';
import { toast } from 'react-toastify';
import agent from '../../../app/api/agent';
import { useAppDispatch } from '../../../app/store/configureStore';
import { setAdminOrder } from './adminOrdersSlice';

interface Props {
    open: boolean;
    onClose: () => void;
    order: Order;
}

const BIZIN_BASE_URL = "https://system.bizin.pl/invoice.html?type=pdf&";

export default function AdminOrderInvoiceUrlDialog({ open, onClose, order }: Props) {
    const [url, setUrl] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useAppDispatch();

    // Initialize form with existing data when dialog opens
    useEffect(() => {
        if (open) {
            setUrl(order.bizInInvoiceURL || '');
            setAccountNumber(order.accountNumber || '');
        }
    }, [open, order.bizInInvoiceURL, order.accountNumber]);

    const handleClose = () => {
        setUrl('');
        setAccountNumber('');
        onClose();
    };

    // Validate account number format: XXX/XX/XXXX
    const validateAccountNumber = (value: string): boolean => {
        const regex = /^\d{3}\/\d{2}\/\d{4}$/;
        return regex.test(value);
    };

    const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        
        // Allow only digits and forward slashes
        const cleanInput = input.replace(/[^\d/]/g, '');
        
        // Extract digits only for counting
        const digitsOnly = cleanInput.replace(/\D/g, '');
        
        // Limit to 9 digits maximum (3+2+4)
        if (digitsOnly.length > 9) {
            return; // Don't update if more than 9 digits
        }
        
        // Apply automatic formatting based on digit count
        let formatted = '';
        
        if (digitsOnly.length > 0) {
            // First 3 digits
            formatted = digitsOnly.substring(0, 3);
            
            if (digitsOnly.length > 3) {
                // Add first slash and next 2 digits
                formatted += '/' + digitsOnly.substring(3, 5);
                
                if (digitsOnly.length > 5) {
                    // Add second slash and remaining digits (up to 4)
                    formatted += '/' + digitsOnly.substring(5, 9);
                }
            }
        }
        
        // If user is manually typing and the input already has correct structure, preserve it
        // This handles cases where user types "/" manually
        if (cleanInput.length <= 11 && // Max length XXX/XX/XXXX = 11 chars
            cleanInput.match(/^\d{0,3}\/?(\d{0,2})?\/?(\d{0,4})?$/)) {
            setAccountNumber(cleanInput);
        } else {
            setAccountNumber(formatted);
        }
    };

    const extractParameters = (inputUrl: string): string | null => {
        try {
            // If user enters only parameters (starting with idprint, inst, or klucz)
            if (inputUrl.includes('idprint=') || inputUrl.includes('inst=') || inputUrl.includes('klucz=')) {
                // If it's already a full URL, extract parameters after 'type=pdf&'
                if (inputUrl.includes(BIZIN_BASE_URL)) {
                    const urlObj = new URL(inputUrl);
                    const params = new URLSearchParams(urlObj.search);
                    
                    const idprint = params.get('idprint');
                    const inst = params.get('inst');
                    const klucz = params.get('klucz');
                    const tExt = params.get('tExt') || 'regular';
                    
                    if (idprint && inst && klucz) {
                        return `idprint=${idprint}&inst=${inst}&klucz=${klucz}&tExt=${tExt}`;
                    }
                }
                // If it's just parameters, validate and use them
                else if (!inputUrl.startsWith('http')) {
                    // Remove leading & or ? if present
                    let cleanParams = inputUrl.replace(/^[?&]/, '');
                    
                    // Check if required parameters are present
                    const hasIdprint = cleanParams.includes('idprint=');
                    const hasInst = cleanParams.includes('inst=');
                    const hasKlucz = cleanParams.includes('klucz=');
                    
                    if (hasIdprint && hasInst && hasKlucz) {
                        // Add tExt=regular if not present
                        if (!cleanParams.includes('tExt=')) {
                            cleanParams += '&tExt=regular';
                        }
                        return cleanParams;
                    }
                }
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    const handleSubmit = async () => {
        if (!url.trim()) {
            toast.error('Wprowadź URL faktury lub parametry');
            return;
        }

        if (!accountNumber.trim()) {
            toast.error('Wprowadź numer faktury');
            return;
        }

        if (!validateAccountNumber(accountNumber)) {
            toast.error('Numer faktury musi mieć format: XXX/XX/XXXX (np. 029/06/2025)');
            return;
        }

        let finalUrl = '';

        // Check if it's already a complete BizIn URL
        if (url.startsWith(BIZIN_BASE_URL)) {
            finalUrl = url;
        } else {
            // Try to extract parameters
            const parameters = extractParameters(url);
            if (parameters) {
                finalUrl = BIZIN_BASE_URL + parameters;
            } else {
                toast.error('Nieprawidłowy format URL. Wprowadź kompletny URL BizIn lub parametry (idprint, inst, klucz)');
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const updatedOrder = await agent.Admin.Orders.updateInvoiceUrl(order.publicId, finalUrl, accountNumber);
            toast.success('URL faktury i numer faktury zostały zaktualizowane');
            // Update the order in the Redux store immediately
            dispatch(setAdminOrder(updatedOrder));
            handleClose();
        } catch (error: any) {
            console.error('Error updating invoice URL:', error);
            if (error.response?.data?.title) {
                toast.error(error.response.data.title);
            } else {
                toast.error('Wystąpił błąd podczas aktualizacji danych faktury');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const isValidUrl = url.trim() && (
        url.startsWith(BIZIN_BASE_URL) || 
        extractParameters(url) !== null
    );

    const isValidAccountNumber = accountNumber.trim() && validateAccountNumber(accountNumber);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: { minHeight: '400px' }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                pb: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinkIcon color="primary" />
                    <Typography variant="h6">
                        Aktualizuj dane faktury BizIn
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Zamówienie: <strong>#{order.publicId}</strong> - {order.customeClientServiceName}
                    </Typography>
                    
                    {order.bizInInvoiceURL && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Obecny URL:</strong><br />
                                <a href={order.bizInInvoiceURL} target="_blank" rel="noopener noreferrer">
                                    {order.bizInInvoiceURL}
                                </a>
                            </Typography>
                        </Alert>
                    )}

                    {order.accountNumber && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Obecny numer faktury:</strong> {order.accountNumber}
                            </Typography>
                        </Alert>
                    )}
                </Box>

                <TextField
                    fullWidth
                    label="Numer faktury"
                    placeholder="029/06/2025"
                    value={accountNumber}
                    onChange={handleAccountNumberChange}
                    sx={{ mb: 3 }}
                    helperText="Format: XXX/XX/XXXX (3 cyfry / 2 cyfry / 4 cyfry)"
                    inputProps={{
                        maxLength: 11 // XXX/XX/XXXX = 11 characters total including slashes
                    }}
                />

                {accountNumber && !validateAccountNumber(accountNumber) && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Nieprawidłowy format numeru faktury. Użyj formatu: XXX/XX/XXXX
                        </Typography>
                    </Alert>
                )}

                <TextField
                    fullWidth
                    label="URL faktury BizIn"
                    placeholder="Wklej kompletny URL lub parametry (idprint=343&inst=11314&klucz=...)"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    multiline
                    rows={4}
                    sx={{ mb: 3 }}
                    helperText={
                        <>
                            <strong>Formaty akceptowane:</strong><br/>
                            • Kompletny URL: https://system.bizin.pl/invoice.html?type=pdf&idprint=343&inst=11314&klucz=...<br/>
                            • Tylko parametry: idprint=343&inst=11314&klucz=mAhBrr3zYLuHxbARAjDjLs7AaTq2A1QA9wiSpouWHaAgS5yT&tExt=regular
                        </>
                    }
                />

                {url && isValidUrl && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Podgląd URL:</strong><br />
                            {url.startsWith(BIZIN_BASE_URL) ? url : BIZIN_BASE_URL + extractParameters(url)}
                        </Typography>
                    </Alert>
                )}

                {url && !isValidUrl && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Nieprawidłowy format. Upewnij się, że URL zawiera parametry: idprint, inst i klucz.
                        </Typography>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button onClick={handleClose} variant="outlined">
                    Anuluj
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={!isValidUrl || !isValidAccountNumber}
                    variant="contained"
                    color="primary"
                >
                    Zapisz dane faktury
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
